import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { useGridApiPaginationAndSort } from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import { CAMPAIGN_MANAGEMENT_ROW_TYPE, DEFAULT_SORT_MODEL } from '@/constants/campaignManagement';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { CAMPAIGN_MANAGEMENT_COLUMNS } from '@/constants/sessionStorage';
import { getTreeDataPath } from '@/helpers/grid';
import { getSessionStorageObjectItem } from '@/helpers/sessionStorage';
import { useAppSelector } from '@/store/hooks';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { resetRowsExpandAfterUpdate, setFilters } from '@/store/slices/campaignManagementSlice';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  getGridStringQuickFilterFn,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NoData from '../components/NoData';
import { getColumns } from './columns';
import GroupingCell from './components/GroupingCell';
import ToolBar from './components/ToolBar';

const CampaignsTable = memo(({ campaignsData, rowCount, loading }) => {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();

  const isAdmin = useAppSelector(selectIsAdmin);
  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);

  const [expandedRowsIds, setExpandedRowsIds] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const setTableFilters = useCallback(filters => dispatch(setFilters(filters)), [dispatch]);

  const { paginationModel, sortModel, handlePaginationModelChange, handleSortModelChange } =
    useGridApiPaginationAndSort({ setFilters: setTableFilters, defaultSortModel: DEFAULT_SORT_MODEL });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    getSessionStorageObjectItem(CAMPAIGN_MANAGEMENT_COLUMNS.VISIBILITY_MODEL),
  );

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const [applyRowsExpandAfterUpdate, totalCampaigns] = useSelector(({ campaignManagement }) => [
    campaignManagement.applyRowsExpandAfterUpdate,
    campaignManagement.totalCampaigns,
  ]);

  useEffect(() => {
    if (applyRowsExpandAfterUpdate && expandedRowsIds.length) {
      const rows = campaignsData || [];
      const currentRowIds = new Set(rows.map(r => r.id));
      const rowsById = new Map(rows.map(r => [r.id, r]));

      const hasChildren = id => {
        const row = rowsById.get(id);
        if (!row) return false;
        if (row.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN) {
          return rows.some(r => r.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM && r.campaignId === row.campaignId);
        }
        return false;
      };

      const safeExpandedIds = expandedRowsIds.filter(id => currentRowIds.has(id) && hasChildren(id));

      safeExpandedIds.forEach(id => {
        apiRef.current.setRowChildrenExpansion(id, true);
      });

      if (safeExpandedIds.length !== expandedRowsIds.length) {
        setExpandedRowsIds(safeExpandedIds);
      }

      dispatch(resetRowsExpandAfterUpdate());
    }
  }, [apiRef, dispatch, expandedRowsIds, applyRowsExpandAfterUpdate, campaignsData]);

  const handleRowExpand = (id, shouldExpand) => {
    setExpandedRowsIds(shouldExpand ? [...expandedRowsIds, id] : expandedRowsIds.filter(prevId => prevId !== id));
    apiRef.current.setRowChildrenExpansion(id, shouldExpand);
  };

  const [columns, setColumns] = useState(getColumns(editMode, undefined, isDV360CampaignManagementEnabled));

  useEffect(() => {
    setColumns(getColumns(editMode, isAdmin, isDV360CampaignManagementEnabled));
  }, [editMode, isAdmin, isDV360CampaignManagementEnabled]);

  const onColumnVisibilityChange = column => () => {
    setColumnVisibilityModel({ ...columnVisibilityModel, [column]: !(columnVisibilityModel[column] ?? true) });
  };

  const onColumnReorder = (sourceIndex, destinationIndex) => {
    const newOrderedColumns = [...columns];
    const [removed] = newOrderedColumns.splice(sourceIndex + 1, 1);
    newOrderedColumns.splice(destinationIndex + 1, 0, removed);
    setColumns(newOrderedColumns);
  };

  const onColumnManagementReset = () => {
    sessionStorage.removeItem(CAMPAIGN_MANAGEMENT_COLUMNS.VISIBILITY_MODEL);
    sessionStorage.removeItem(CAMPAIGN_MANAGEMENT_COLUMNS.ORDER);
    setColumnVisibilityModel({});
    setColumns(getColumns(editMode, isAdmin, isDV360CampaignManagementEnabled));
  };

  const saveColumnConfig = () => {
    sessionStorage.setItem(CAMPAIGN_MANAGEMENT_COLUMNS.VISIBILITY_MODEL, JSON.stringify(columnVisibilityModel));
    sessionStorage.setItem(
      CAMPAIGN_MANAGEMENT_COLUMNS.ORDER,
      columns.map(({ _order }) => _order),
    );
  };

  return (
    <Grid
      apiRef={apiRef}
      treeData
      loading={loading}
      rows={campaignsData}
      rowCount={rowCount}
      columns={columns}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      getTreeDataPath={getTreeDataPath}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableCellSelection
      showCellVerticalBorder
      showColumnVerticalBorder
      autosizeOnMount
      autosizeOptions={EXPAND_OPTIONS}
      checkboxSelection
      columnHeaderHeight={30}
      experimentalFeatures={{ columnPinning: true }}
      pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD] }}
      groupingColDef={{
        headerName: 'Campaign Name',
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => row.campaignName,
        display: 'flex',
        renderCell: params => <GroupingCell {...params} handleExpand={handleRowExpand} />,
        getApplyQuickFilterFn: getGridStringQuickFilterFn,
      }}
      initialState={{
        ...campaignsData.initialState,
        pagination: { paginationModel: { pageSize: 15 } },
      }}
      pagination
      paginationMode='server'
      sortingMode='server'
      slots={{
        pagination: ServerSidePagination,
        toolbar: ToolBar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
        noRowsOverlay: NoData,
        noResultsOverlay: NoData,
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
      slotProps={{
        pagination: {
          loading,
          rowCount,
        },
        footer: {
          className: 'relative',
        },
        toolbar: {
          counter: totalCampaigns,
          loading,
          editMode,
          toggleEditMode,
          columns: columns.slice(1),
          onColumnReorder,
          columnVisibilityModel,
          onColumnVisibilityChange,
          onColumnManagementReset,
          saveColumnConfig,
        },
      }}
      sx={{ '--DataGrid-overlayHeight': '500px' }}
    />
  );
});

CampaignsTable.propTypes = {
  campaignsData: PropTypes.array,
  rowCount: PropTypes.number,
  loading: PropTypes.bool,
};

export default CampaignsTable;
